import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { cn } from '../../../../../../helpers/utils'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { IconChoices } from '../../../../../../components/common/Icon/Icon'
import { ButtonVariant } from '../../../../../../components/common/Button/Button'
import { ITicketBoard } from '../../../../../../interfaces/models/ticket.interfaces'
import { LinkButton } from '../../../../../../components/common/LinkButton'
import { SPACE_TRANSLATION_KEY } from '../../../../../../helpers/ticketSpace'
import { useCurrentSociety } from '../../../../../../hooks/useCurrentSociety'
import { userQueries } from '../../../../../../api/users'
import { unitQueries } from '../../../../../../api/units'
import { entranceQueries } from '../../../../../../api/society-entrances'
import { formatDate } from '../../../../../../helpers/date'

export const ForwardTicket = ({
  className,
  ticket,
}: {
  className?: string
  ticket?: ITicketBoard
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const customerId = ticket?.customer?.id

  const { society, isLoading: societyLoading } = useCurrentSociety()
  const { data: customerUser, isLoading: userLoading } = useQuery(
    userQueries.getUser(customerId, ticket?.societyId)
  )
  const { data: userUnits, isLoading: unitsLoading } = useQuery(
    unitQueries.getUserUnits(customerId)
  )
  const { data: entrances, isLoading: entrancesLoading } = useQuery(
    entranceQueries.getEntrancesPerSociety(ticket?.societyId)
  )

  const isLoading =
    societyLoading ||
    entrancesLoading ||
    (!!customerId && (userLoading || unitsLoading))

  const createMailTo = (): string => {
    const t = (key: string, options?: Record<string, unknown>): string =>
      translate(`ticketBoardSidebar.forwardTicket.${key}`, {
        context: ticket?.variant,
        ...options,
      })

    const placeholder = t('missing')
    const val = (value?: string): string =>
      value && value.trim() ? value.trim() : placeholder

    const subject = `${t('subjectPrefix')}#${ticket?.ticketNumber} ${ticket?.title ?? ''}`

    const unit = userUnits?.find((u) => u.societyId === ticket?.societyId)
    const entrance = entrances?.find((e) => e._id === unit?.entranceId)
    const resident = ticket?.customer
      ? `${ticket.customer.name} ${ticket.customer.surname}`.trim()
      : undefined
    const mobile = customerUser?.mobile?.trim() || undefined
    const societyName = society?.name
    const area = ticket?.space
      ? translate(SPACE_TRANSLATION_KEY[ticket.space])
      : undefined
    const createdAt = ticket?.createdAt
      ? formatDate(new Date(ticket.createdAt), 'long')
      : placeholder

    const lines = [
      t('bodyIntro', { date: createdAt }),
      '',
      `${t('labelSociety')}: ${val(societyName)}`,
      `${t('labelResident')}: ${val(resident)}`,
      `${t('labelAddress')}: ${val(entrance?.addressStreet)}`,
      `${t('labelUnit')}: ${val(unit?.title)}`,
      `${t('labelEmail')}: ${val(customerUser?.email)}`,
      `${t('labelMobile')}: ${val(mobile)}`,
      '',
      ...(area ? [t('bodyArea'), area, ''] : []),
      `${t('labelTitle')}:`,
      val(ticket?.title),
      '',
      `${t('labelDescription')}:`,
      val(ticket?.description),
      '',
      t('greeting'),
      t('signature', { societyName: val(societyName) }),
    ]

    const body = lines.join('\n')

    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <div className={cn('w-full', className)}>
      <LinkButton
        url={createMailTo()}
        variant={ButtonVariant.PRIMARY}
        label={translate('ticketBoardSidebar.forwardTicket.button')}
        icon={IconChoices.SEND}
        disabled={isLoading}
        loading={isLoading}
      />
    </div>
  )
}
